import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import { ThemeProvider, createTheme, CssBaseline, Box, CircularProgress, Button, Typography } from '@mui/material';
import LoginPage from './pages/Login';
import SignupPage from './pages/Signup';
import ForgotPasswordPage from './pages/ForgotPassword';
import ResetPasswordPage from './pages/ResetPassword';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Lazy loaded pages for code splitting
const DeveloperDashboardPage = React.lazy(() => import('./pages/DeveloperDashboardPage'));
const AppRegistrationPage = React.lazy(() => import('./pages/AppRegistration'));
const PlacementManagementPage = React.lazy(() => import('./pages/PlacementManagementPage'));
const DeveloperAnalyticsPage = React.lazy(() => import('./pages/DeveloperAnalyticsPage'));
const MyAppsPage = React.lazy(() => import('./pages/MyAppsPage'));
const EarningsPage = React.lazy(() => import('./pages/EarningsPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const AdUnitsPage = React.lazy(() => import('./pages/AdUnitsPage'));
const PayoutsPage = React.lazy(() => import('./pages/PayoutsPage'));
const NotificationsPage = React.lazy(() => import('./pages/Notifications'));
const LandingPage = React.lazy(() => import('./pages/Landing'));
const NewsPage = React.lazy(() => import('./pages/News'));
const BlogsPage = React.lazy(() => import('./pages/Blogs'));
const DocsPage = React.lazy(() => import('./pages/Docs'));
const SupportPage = React.lazy(() => import('./pages/Support'));

// Create custom theme aligned with design system
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#ff8b42',
      light: '#ffae78',
      dark: '#f97316',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#8f63ff',
      light: '#b095ff',
      dark: '#6f46e9',
    },
    background: {
      default: '#07070c',
      paper: 'rgba(20, 20, 36, 0.58)',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(239, 241, 255, 0.74)',
      disabled: 'rgba(255, 255, 255, 0.5)',
    },
    divider: 'rgba(255, 255, 255, 0.12)',
    success: {
      main: '#14B8A6',
      light: '#2DD4BF',
    },
    warning: {
      main: '#F59E0B',
      light: '#FBBF24',
    },
    error: {
      main: '#EF4444',
      light: '#F87171',
    },
    info: {
      main: '#3B82F6',
      light: '#60A5FA',
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '1rem',
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #ff8b42, #8f63ff)',
          '&:hover': {
            background: 'linear-gradient(135deg, #ff7a2f, #7a4dff)',
            boxShadow: '0 4px 14px rgba(143, 99, 255, 0.35)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: 'rgba(20, 20, 36, 0.58)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: 'rgba(20, 20, 36, 0.58)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.06)',
            },
            '&.Mui-focused': {
              backgroundColor: 'rgba(255, 255, 255, 0.06)',
            },
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          '&.Mui-selected': {
            background: 'linear-gradient(135deg, rgba(255, 139, 66, 0.16), rgba(143, 99, 255, 0.14))',
            color: '#ffd8bf',
            '&:hover': {
              background: 'linear-gradient(135deg, rgba(255, 139, 66, 0.22), rgba(143, 99, 255, 0.2))',
            },
          },
        },
      },
    },
  },
});

// Protected Route wrapper
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ background: '#07070c' }}
      >
        <CircularProgress sx={{ color: '#ff8b42' }} />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// 404 Not Found Page Component
const NotFoundPage = () => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    minHeight="100vh"
    sx={{
      background: 'radial-gradient(circle at 50% 30%, rgba(143, 99, 255, 0.2) 0%, #07070c 55%)',
      color: '#fff',
    }}
  >
    <Typography
      variant="h1"
      sx={{
        fontSize: { xs: '4rem', md: '6rem' },
        fontWeight: 800,
        background: 'linear-gradient(135deg, #ff8b42, #8f63ff)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        mb: 2,
      }}
    >
      404
    </Typography>
    <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1 }}>
      Page Not Found
    </Typography>
    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.5)', mb: 4 }}>
      The page you're looking for doesn't exist or has been moved.
    </Typography>
    <Button
      variant="contained"
      color="primary"
      href="/dashboard"
      sx={{
        background: 'linear-gradient(135deg, #ff8b42, #8f63ff)',
        '&:hover': {
          background: 'linear-gradient(135deg, #ff7a2f, #7a4dff)',
        },
      }}
    >
      Back to Dashboard
    </Button>
  </Box>
);

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          {/* Skip to content link for accessibility */}
          <a href="#main-content" className="skip-to-content">
            Skip to main content
          </a>
          <Suspense fallback={
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" sx={{ background: '#07070c' }}>
              <CircularProgress sx={{ color: '#ff8b42' }} />
            </Box>
          }>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              {/* Protected Routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DeveloperDashboardPage />
                </ProtectedRoute>
              } />
              <Route path="/apps/register" element={
                <ProtectedRoute>
                  <AppRegistrationPage />
                </ProtectedRoute>
              } />
              <Route path="/apps/:appId/ad-units" element={
                <ProtectedRoute>
                  <PlacementManagementPage />
                </ProtectedRoute>
              } />
              <Route path="/analytics" element={
                <ProtectedRoute>
                  <DeveloperAnalyticsPage />
                </ProtectedRoute>
              } />
              <Route path="/apps" element={
                <ProtectedRoute>
                  <MyAppsPage />
                </ProtectedRoute>
              } />
              <Route path="/earnings" element={
                <ProtectedRoute>
                  <EarningsPage />
                </ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              } />
              <Route path="/ad-units" element={
                <ProtectedRoute>
                  <AdUnitsPage />
                </ProtectedRoute>
              } />
              <Route path="/payouts" element={
                <ProtectedRoute>
                  <PayoutsPage />
                </ProtectedRoute>
              } />
              <Route path="/notifications" element={
                <ProtectedRoute>
                  <NotificationsPage />
                </ProtectedRoute>
              } />
              <Route path="/news" element={<NewsPage />} />
              <Route path="/news/:articleId" element={<NewsPage />} />
              <Route path="/blogs" element={<BlogsPage />} />
              <Route path="/blogs/:articleId" element={<BlogsPage />} />
              <Route path="/docs" element={<DocsPage />} />
              <Route path="/support" element={<SupportPage />} />

              {/* Default Route */}
              <Route path="/" element={<LandingPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
